<template>
    <v-row no-gutters>
        <v-col cols="12">
            <v-divider class="my-4"></v-divider>
            <h4 class="mb-3">Attached Files</h4>
            <v-data-table
                v-if="displayFiles.length > 0"
                :headers="fileHeaders"
                :items="displayFiles"
                item-key="id"
                class="elevation-0 mb-3"
                dense
                hide-default-footer
                disable-pagination
            >
                <template v-slot:[`item.name`]="{ item }">
                    <span
                        class="file-link"
                        style="cursor: pointer;"
                        @click="openFileInNewWindow(item)"
                    >
                        {{ item.name }}
                    </span>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-btn small icon color="error" @click="removeFile(item)">
                        <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                </template>
            </v-data-table>
            <input
                ref="nativeFileInput"
                type="file"
                multiple
                class="d-none"
                :accept="fileExtensions.map(e => '.' + e).join(',')"
                @change="handleFilesSelected"
            />
            <v-text-field
                value=" "
                prepend-icon="mdi-paperclip"
                readonly
                hide-details
                :error="!!fileAttachmentError"
                style="cursor: pointer;"
                @click="onAddFilesFieldClick"
            >
                <template v-slot:label>
                    <span>Add files</span>
                </template>
                <template v-slot:prepend-inner>
                    <div class="d-flex flex-wrap align-center">
                        <v-chip
                            v-for="(sf, i) in stagingFiles"
                            :key="sf.id"
                            small
                            close
                            class="mr-1 my-1"
                            @click:close="removeStagingFileAt(i)"
                        >
                            {{ sf.name }}
                        </v-chip>
                    </div>
                </template>
            </v-text-field>
            <div
                v-if="fileAttachmentError"
                class="error--text text-caption mt-1"
                role="alert"
            >
                {{ fileAttachmentError }}
            </div>
        </v-col>
    </v-row>
</template>

<script>
import { openFile } from '@/services/storage/openFile.js'

export default {
    name: 'PartNumberAttachments',
    props: {
        value: {
            type: Array,
            default: () => [],
        },
        partNumberCode: {
            type: String,
            default: '',
        },
        workOrder: {
            type: Object,
            default: () => ({}),
        },
        fileExtensions: {
            type: Array,
            default: () => ['pdf', 'dxf', 'stp'],
        },
    },
    data() {
        return {
            localFiles: [],
            filesSnapshot: '',
            stagingFiles: [],
            fileHeaders: [
                { text: 'File Name', value: 'name', sortable: false },
                {
                    text: 'Actions',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: '100',
                },
            ],
            fileAttachmentError: '',
        }
    },
    computed: {
        displayFiles() {
            return this.normalizeFilesForDisplay(this.localFiles)
        },
    },
    watch: {
        value: {
            handler(files) {
                const snapshot = this.serializeFilesList(files)
                if (snapshot === this.filesSnapshot) return
                this.filesSnapshot = snapshot
                this.localFiles = Array.isArray(files) ? [...files] : []
            },
            deep: true,
            immediate: true,
        },
        partNumberCode() {
            this.fileAttachmentError = ''
        },
    },
    methods: {
        serializeFilesList(files) {
            const list = Array.isArray(files) ? files : []
            return list
                .map((f, i) => this.fileEntryKey(f, i))
                .sort()
                .join('\n')
        },
        commitFiles(nextFiles) {
            const next = Array.isArray(nextFiles) ? [...nextFiles] : []
            this.localFiles = next
            this.filesSnapshot = this.serializeFilesList(next)
            this.$emit('input', next)
        },
        fileNameFromUrl(url) {
            if (!url || typeof url !== 'string') return ''
            const parts = url.split('/')
            return parts[parts.length - 1] || ''
        },
        fileEntryKey(entry, index = 0) {
            if (typeof entry === 'string') return entry
            if (!entry || typeof entry !== 'object') return `file-${index}`
            const name =
                entry.name ||
                this.fileNameFromUrl(entry.url) ||
                (typeof entry.id === 'string' ? entry.id : '')
            return entry.id || name || `file-${index}`
        },
        normalizeFilesForDisplay(files) {
            const safeFiles = Array.isArray(files) ? files : []
            return safeFiles.map((f, index) => {
                if (typeof f === 'string') {
                    return { name: f, id: f, url: undefined }
                }
                const name = f.name || this.fileNameFromUrl(f.url) || ''
                const id = this.fileEntryKey(f, index)
                return { name, id, url: f.url }
            })
        },
        onAddFilesFieldClick() {
            this.fileAttachmentError = ''
            this.$refs.nativeFileInput.click()
        },
        handleFilesSelected(event) {
            const picked = Array.from(event.target.files || [])
            event.target.value = ''
            this.fileAttachmentError = ''
            if (!picked.length) return

            const expectedCode = (this.partNumberCode || '').trim()
            if (!expectedCode) {
                this.fileAttachmentError =
                    'Select a part number before attaching files. File names must be {partNumberCode}.pdf|.dxf|.stp.'
                return
            }

            let next = [...this.localFiles]

            for (const file of picked) {
                const { base, ext } = this.splitFileNameAtLastDot(file.name)
                if (!ext || !this.fileExtensions.includes(ext)) {
                    const allowed = this.fileExtensions
                        .map(e => '.' + e)
                        .join(', ')
                    this.fileAttachmentError = `"${file.name}" has an invalid extension. Allowed: ${allowed}.`
                    return
                }
                if (base !== expectedCode) {
                    this.fileAttachmentError = `"${file.name}" must be named "${expectedCode}.${ext}" (base name must match the part number code).`
                    return
                }
                const fileObj = {
                    id: file.name,
                    name: file.name,
                    file,
                    uploaded: false,
                }

                const stagingIdx = this.stagingFiles.findIndex(
                    sf => this.splitFileNameAtLastDot(sf.name).ext === ext
                )
                if (stagingIdx > -1) {
                    this.stagingFiles.splice(stagingIdx, 1, fileObj)
                } else {
                    this.stagingFiles.push(fileObj)
                }

                const itemIdx = next.findIndex(f => {
                    const n = typeof f === 'string' ? f : f?.name
                    return this.splitFileNameAtLastDot(n).ext === ext
                })
                if (itemIdx > -1) {
                    next.splice(itemIdx, 1, fileObj)
                } else {
                    next.push(fileObj)
                }
            }

            this.commitFiles(next)
        },
        removeStagingFileAt(index) {
            const sf = this.stagingFiles[index]
            if (!sf) return
            const { ext } = this.splitFileNameAtLastDot(sf.name)
            this.stagingFiles.splice(index, 1)
            if (!ext) return

            const itemIdx = this.localFiles.findIndex(f => {
                const n = typeof f === 'string' ? f : f?.name
                return this.splitFileNameAtLastDot(n).ext === ext
            })
            if (itemIdx > -1) {
                this.commitFiles(
                    this.localFiles.filter((_, i) => i !== itemIdx)
                )
            }
        },
        removeFile(fileToRemove) {
            const rowKey = fileToRemove?.id || fileToRemove?.name
            if (!rowKey || !this.localFiles.length) return

            const displayIndex = this.displayFiles.findIndex(
                row => (row.id || row.name) === rowKey
            )
            if (displayIndex < 0) return

            const removed = this.localFiles[displayIndex]
            const n = typeof removed === 'string' ? removed : removed?.name
            const { ext } = this.splitFileNameAtLastDot(n || '')

            this.commitFiles(
                this.localFiles.filter((_, i) => i !== displayIndex)
            )

            if (ext) {
                const sIdx = this.stagingFiles.findIndex(
                    sf => this.splitFileNameAtLastDot(sf.name).ext === ext
                )
                if (sIdx > -1) this.stagingFiles.splice(sIdx, 1)
            }
        },
        async openFileInNewWindow(fileEntry) {
            if (!fileEntry || !fileEntry.name) return
            try {
                let path, fileName
                if (fileEntry.url) {
                    const parts = fileEntry.url.split('/')
                    fileName = parts.pop()
                    path = parts.join('/')
                } else if (this.workOrder && this.workOrder.id) {
                    const company = JSON.parse(
                        localStorage.getItem('company') || '{}'
                    )
                    const companyId = company.id || company
                    path = `${companyId}/workOrders/${this.workOrder.id}/items`
                    fileName = fileEntry.name
                } else {
                    return
                }
                await openFile(fileName, path)
            } catch (error) {
                const msg = error.message || 'Error opening file'
                this.fileAttachmentError = msg
            }
        },
        splitFileNameAtLastDot(name) {
            if (!name || typeof name !== 'string') return { base: '', ext: '' }
            const dot = name.lastIndexOf('.')
            if (dot < 1) return { base: '', ext: '' }
            return {
                base: name.slice(0, dot),
                ext: name.slice(dot + 1).toLowerCase(),
            }
        },
    },
}
</script>

<style scoped>
.file-link {
    cursor: pointer;
    color: var(--v-primary-base);
    text-decoration: underline;
}
.file-link:hover {
    opacity: 0.85;
}
</style>
